import { useCallback, useMemo } from 'react';
import { useApi } from '@hooks/services/api';
import { getItemImageUrl } from '@utils/itemImageUrl';
import { useLogger } from '@providers/loggerProvider';
import { createHandleError } from '@hooks/services/_useBaseService';
import { ServiceError } from '@types/ServiceError';

const requirePositiveInteger = (value, fieldName) => {
    if (!Number.isInteger(value) || value < 1) {
        throw new Error(`${fieldName} must be a positive integer`);
    }
};

const requireJobId = (value, fieldName) => {
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`${fieldName} must be a non-empty string`);
    }
};

export const useUserSetsService = () => {
    const { apiRequest } = useApi();
    const logger = useLogger('useUserSetsService');

    const handleError = useMemo(() => createHandleError(), []);

    /**
     * Get all user sets or a specific user set by ID
     * @param {number|null} userSetId - The ID of the user set (optional, if not provided, returns all user sets)
     * @param {Object} [options={}] - Query options
     * @param {boolean} [options.includeItems] - Whether to include set items in the response
     * @param {boolean} [options.includeStats] - Whether to include fast aggregate completion stats instead of full items (list view optimization)
     * @param {boolean} [options.includeCompletionCost] - Whether to include set completion aggregates (completion_parts_cost, missing_pieces, missing_lots)
     * @param {boolean} [options.includeMinifigValue] - Whether to include the minifig-only aggregates (minifig_value, minifig_lots, minifig_unpriced_lots, minifig_priced_at)
     * @param {boolean} [options.includeCompletability] - Whether to include completability_score, completability_band, hunt_pieces and critical_lots
     * @param {boolean} [options.includeTrackedInSets] - Whether to include tracked_in_sets, the numbers of the user's tracked SET builds that still need this row's item
     * @param {boolean} [options.includeListings] - Whether to include UserSetListingModels (marketplace listing rows)
     * @param {string} [options.type] - Parent item type filter (SET|MINIFIG); collection calls only
     * @returns {Promise<Object|Array>} - The user set or user sets
     */
    const getUserSets = useCallback(async (userSetId = null, {
        includeItems = false,
        includeStats = false,
        includeCompletionCost = false,
        includeMinifigValue = false,
        includeCompletability = false,
        includeTrackedInSets = false,
        includeListings = false,
        type = null
    } = {}) => {

        try {
            // Build query parameters
            const queryParams = [];
            if (includeItems) queryParams.push('include_items=true');
            if (includeStats) queryParams.push('include_stats=true');
            if (includeCompletionCost) queryParams.push('include_completion_cost=true');
            if (includeMinifigValue) queryParams.push('include_minifig_value=true');
            if (includeCompletability) queryParams.push('include_completability=true');
            if (includeTrackedInSets) queryParams.push('include_tracked_in_sets=true');
            if (includeListings) queryParams.push('include_listings=true');
            // Collection calls only: /userSets/{id} does not declare the type
            // param (findById never applies buildSimpleWhere) and would 400.
            if (type && !userSetId) queryParams.push(`type=${type}`);

            const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
            
            // Determine endpoint based on whether userSetId is provided
            const endpoint = userSetId ? `/userSets/${userSetId}${queryString}` : `/userSets${queryString}`;
            
            const response = await apiRequest(endpoint, 'GET');
            
            // Extract data from the API response structure
            const responseData = response?.data || response || [];
            
            // Transform nested Sequelize UserSetItemModels into flat user_set_items
            const flattenSetItems = (userSetItemModels, parentUserSetId) => {
                if (!Array.isArray(userSetItemModels)) return [];
                return userSetItemModels.map(usi => {
                    // Tracked requirements own their lot. Catalog rows are optional provenance.
                    const setItem = usi.SetItemModel;
                    const lot = usi.LotModel;
                    if (!lot) {
                        throw new Error(`User set item ${usi.id} has no lot`);
                    }

                    const inventoryItems = (lot.userInventoryItems || []).map(inv => ({
                        id: inv.id,
                        quantity: inv.quantity,
                        condition: inv.condition,
                        location_id: inv.user_inventory_item_location_id,
                        location_name: inv.UserInventoryItemLocationModel?.name || null
                    }));
                    const inventory_qty = inventoryItems.reduce((sum, inv) => sum + (inv.quantity || 0), 0);

                    return {
                        id: usi.id,
                        user_set_id: parentUserSetId,
                        item_number: lot.item_number,
                        item_name: lot.ItemModel?.name,
                        item_type: lot.ItemModel?.type,
                        is_decorated: lot.ItemModel?.is_decorated || false,
                        color_id: lot.color_id,
                        color_name: lot.ColorModel?.name,
                        color_hex: lot.ColorModel?.html,
                        category_name: lot.ItemModel?.item_category?.name ?? null,
                        weight: lot.ItemModel?.weight,
                        // Piece rarity: the band WORD is computed by the API from
                        // shared/completability.json, which ui/src cannot import. n_sets
                        // rides along so the column can sort at full resolution instead
                        // of on five discrete words.
                        rarity_band: lot.rarity_band,
                        n_sets: lot.n_sets,
                        set_qty: setItem ? setItem.qty : null,
                        is_counterpart: setItem ? setItem.is_counterpart === true : false,
                        has_children: lot.ItemModel?.has_children === true,
                        qty_have: usi.qty_have,
                        total_qty_needed: usi.total_qty_needed,
                        current_qty_needed: usi.current_qty_needed,
                        unpicked_qty: usi.unpicked_qty || 0,
                        is_verified: usi.is_verified || false,
                        on_order: usi.on_order,
                        verification_state: usi.verification_state,
                        confirmed_qty: usi.confirmed_qty,
                        verification_revision: usi.verification_revision,
                        used_avg_sold_price_us: lot.used_avg_sold_price_us,
                        new_avg_sold_price_us: lot.new_avg_sold_price_us,
                        used_avg_inventory_price_us: lot.used_avg_inventory_price_us,
                        new_avg_inventory_price_us: lot.new_avg_inventory_price_us,
                        inventory_qty,
                        userInventoryItems: inventoryItems
                    };
                });
            };

            // Process the response to add image URLs
            const processData = (data) => {
                if (!data) return data;

                if (Array.isArray(data)) {
                    return data.map(item => ({
                        ...item,
                        // Full item object: MINIFIG image resolution needs
                        // thumbnail_url/alternate_number, which the /userSets
                        // include carries.
                        image: getItemImageUrl(item.SetModel?.item ?? null),
                        user_set_items: flattenSetItems(item.UserSetItemModels, item.id)
                    }));
                } else {
                    return {
                        ...data,
                        image: getItemImageUrl(data.SetModel?.item ?? null),
                        user_set_items: flattenSetItems(data.UserSetItemModels, data.id)
                    };
                }
            };
            
            // If fetching a single user set, the API might return an array with one item or a single object
            let result;
            if (userSetId) {
                result = Array.isArray(responseData) ? responseData[0] : responseData;
                result = processData(result);
            } else {
                // For all user sets, ensure we return an array
                result = processData(Array.isArray(responseData) ? responseData : []);
            }
            
            return result;
        } catch (error) {
            console.error('Error fetching user set(s):', error);
            throw handleError(error);
        }
    }, [apiRequest, handleError]);

    /**
     * Add a new user set
     * @param {Object} setData - The set data to add
     * @param {number} setData.set_id - The set ID (required)
     * @param {number} [setData.qty_needed] - Number of independent copies to create (default 1) —
     *   a create-time copy count, not a per-row multiplier. The response may
     *   carry more than one created row; only the first is unwrapped here.
     * @param {number} [setData.user_inventory_item_location_id] - Location ID
     * @returns {Promise<Object>} - The first added user set
     */
    const addUserSet = async (setData) => {
        try {
            logger.info('Adding user set', 'addUserSet', { setData });

            const response = await apiRequest('/userSets', 'POST', setData);

            if (!response || !response.success) {
                throw new Error('Failed to add set');
            }

            const responseData = Array.isArray(response.data) ? response.data[0] : response.data || {};
            return {
                ...responseData,
                image: getItemImageUrl(responseData.SetModel?.item ?? null)
            };
        } catch (error) {
            console.error('Error adding user set:', error);
            throw handleError(error);
        }
    };

    /**
     * Update a user set
     * @param {Object} setData - The set data to update
     * @returns {Promise<Object>} - The updated user set
     */
    const updateUserSet = async (setData) => {
        try {
            const userSetId = setData.id;
            const { id: _id, ...updateFields } = setData;
            if (!userSetId) {
                throw new Error('User set ID is required for update');
            }

            const response = await apiRequest(`/userSets/${userSetId}`, 'PUT', updateFields);

            if (!response.data || typeof response.data !== 'object') {
                throw new Error('Unexpected response shape from updateUserSet');
            }

            // { user_set, user_set_items } — qty_needed is create-only now
            // (a copy count consumed once by createUserSet, never a column
            // PUT can edit), so user_set_items is always empty here.
            return response.data;
        } catch (error) {
            console.error('Error updating user set:', error);
            throw handleError(error);
        }
    };

    /**
     * Delete a user set
     * @param {number|Array<number>} userSetId - The ID of the user set to delete or an array of IDs
     * @returns {Promise<void>}
     */
    const deleteUserSet = async (userSetId) => {
        try {
            if (Array.isArray(userSetId)) {
                // Delete each user set individually - API only supports DELETE /:id
                await Promise.all(userSetId.map(id => apiRequest(`/userSets/${id}`, 'DELETE')));
            } else {
                // Single ID - DELETE /userSets/:id
                await apiRequest(`/userSets/${userSetId}`, 'DELETE');
            }
        } catch (error) {
            // If the error is a 404, the item doesn't exist on the server
            // so we should consider the deletion successful
            if (error.response && error.response.status === 404) {
                return;
            }
            
            console.error('Error deleting user set(s):', error);
            throw handleError(error);
        }
    };

    /**
     * Get items for a user set
     * @param {number} userSetId - The ID of the user set
     * @returns {Promise<Array>} - The user set items
     */
    const getUserSetItems = async (userSetId) => {
        try {
            // Fetch from API
            const data = await apiRequest(`/userSets/${userSetId}`, 'GET', null, { include_items: true });
            
            // Handle different response formats
            let userSetItems = [];
            
            if (Array.isArray(data) && data.length > 0) {
                // If response is an array, get the first item's user_set_items
                userSetItems = data[0].user_set_items || [];
            } else if (data && data.user_set_items) {
                // If response is an object with user_set_items property
                userSetItems = data.user_set_items;
            }
            
            if (!Array.isArray(userSetItems)) {
                console.warn('user_set_items is not an array, converting to empty array');
                userSetItems = [];
            }
            
            return userSetItems;
        } catch (error) {
            console.error('Error in getUserSetItems:', error);
            throw handleError(error);
        }
    };

    /**
     * Update a user set item
     * @param {number} userSetId - The user set ID
     * @param {Object} itemData - The item data to update
     * @returns {Promise<Array>} - The updated user set items
     */
    const updateUserSetItem = async (userSetId, itemData) => {
        const response = await apiRequest(`/userSets/${userSetId}/items`, 'PUT', itemData);
        if (!Array.isArray(response.data)) {
            throw new Error('The update user set item response did not include the changed rows');
        }
        return response.data;
    };

    const getComponentTrackingPreview = useCallback(async (userSetId, itemId) => {
        requirePositiveInteger(userSetId, 'userSetId');
        requirePositiveInteger(itemId, 'itemId');
        const response = await apiRequest(`/userSets/${userSetId}/items/${itemId}/components`, 'GET');
        if (!response?.data || response.data.parent_user_set_item_id !== itemId || typeof response.data.can_convert !== 'boolean' || !Array.isArray(response.data.components)) {
            throw new Error('The component preview response is invalid');
        }
        return response.data;
    }, [apiRequest]);

    const trackSetItemComponents = useCallback(async (userSetId, itemId) => {
        requirePositiveInteger(userSetId, 'userSetId');
        requirePositiveInteger(itemId, 'itemId');
        const response = await apiRequest(`/userSets/${userSetId}/items/${itemId}/components`, 'POST', {});
        if (!response?.data || response.data.parent_user_set_item_id !== itemId) {
            throw new Error('The component tracking response is invalid');
        }
        return response.data;
    }, [apiRequest]);

    const updateVerification = useCallback(async (userSetId, action) => {
        requirePositiveInteger(userSetId, 'userSetId');
        const response = await apiRequest(`/userSets/${userSetId}/verification`, 'POST', action);
        if (!Array.isArray(response.data.items)) {
            throw new Error('The verification response did not include the changed rows');
        }
        return response.data;
    }, [apiRequest]);

    /**
     * Delete user set items — stop tracking pieces inside a user set.
     * The server cancels each item's pending allocations (restoring the
     * reserved inventory) before deleting the rows.
     * @param {number} userSetId - The user set ID
     * @param {Array<number>} itemIds - The user set item IDs to delete
     * @returns {Promise<Object>} The cancelled pending summary
     */
    const deleteUserSetItems = async (userSetId, itemIds) => {
        try {
            const response = await apiRequest(`/userSets/${userSetId}/items`, 'DELETE', itemIds);
            return response.data;
        } catch (error) {
            console.error('Error deleting user set items:', error);
            throw handleError(error);
        }
    };

    /**
     * Allocate inventory pieces to a user set item
     * @param {number} userSetId - The parent user set ID
     * @param {number} userSetItemId - The user set item ID to allocate to
     * @param {number} qtyToAllocate - The quantity to allocate
     * @param {Object} [options={}] - Optional parameters
     * @param {number[]} [options.fromUserInventoryItemIds] - Inventory item IDs to deduct from
     * @param {number[]} [options.fromUserInventoryItemQtys] - Quantities to deduct from each
     * @returns {Promise<Object>} - The updated user set item
     */
    const allocateToSet = async (userSetId, userSetItemId, qtyToAllocate, options = {}) => {
        try {
            logger.debug('Allocating to set', 'allocateToSet', { userSetId, userSetItemId, qtyToAllocate, options });

            const body = { qty_to_allocate: qtyToAllocate, sync_bricklink: true };
            if (options.fromUserInventoryItemIds) {
                body.from_user_inventory_item_ids = options.fromUserInventoryItemIds;
                body.from_user_inventory_item_qtys = options.fromUserInventoryItemQtys;
            }

            const response = await apiRequest(
                `/userSets/${userSetId}/items/${userSetItemId}/allocate`,
                'PUT',
                body
            );

            const data = response?.data || response;
            return Array.isArray(data) ? data[0] : data;
        } catch (error) {
            logger.error('Error allocating to set', 'allocateToSet', { error, userSetId, userSetItemId, qtyToAllocate });
            throw handleError(error);
        }
    };
    // `data` is meaningful when it is null: the API returns null for a user set
    // with no allocation job. Reading it with `||` would hand back the whole
    // envelope, which the caller then stores as if it were a job.
    const requireEnvelopeData = (response, subject) => {
        if (!response || !('data' in response)) {
            throw new Error(`The ${subject} response is invalid`);
        }
        return response.data;
    };

    const enqueueAllocationJob = async (userSetId, request) => {
        requirePositiveInteger(userSetId, 'userSetId');
        if (!request || typeof request !== 'object' || Array.isArray(request)) {
            throw new Error('request must be an object');
        }
        try {
            const response = await apiRequest(`/userSets/${userSetId}/allocation-jobs`, 'POST', request);
            return requireEnvelopeData(response, 'allocation job enqueue');
        } catch (error) {
            // Preserve the API error code and details (e.g. ON_ORDER_ITEMS_IN_SCOPE)
            // so the action handler can branch on them instead of a stripped message.
            if (error?.meta?.error?.code) {
                throw new ServiceError({
                    code: error.meta.error.code,
                    message: error.meta.error.message,
                    statusCode: error.meta.http_status_code,
                    details: error.meta.error.details,
                    serviceName: 'UserSetsService'
                });
            }
            throw handleError(error);
        }
    };

    const getAllocationStatus = async (userSetId) => {
        requirePositiveInteger(userSetId, 'userSetId');
        try {
            const response = await apiRequest(`/userSets/${userSetId}/allocation-status`, 'GET');
            return requireEnvelopeData(response, 'allocation status');
        } catch (error) {
            throw handleError(error);
        }
    };

    const getAllocationJob = async (jobId) => {
        requireJobId(jobId, 'jobId');
        try {
            const response = await apiRequest(`/userSets/allocation/jobs/${jobId}`, 'GET');
            return requireEnvelopeData(response, 'allocation job');
        } catch (error) {
            throw handleError(error);
        }
    };

    const postAllocationJobAction = async (jobId, action) => {
        requireJobId(jobId, 'jobId');
        try {
            const response = await apiRequest(`/userSets/allocation/jobs/${jobId}/${action}`, 'POST');
            return requireEnvelopeData(response, `allocation job ${action}`);
        } catch (error) {
            throw handleError(error);
        }
    };

    const cancelAllocationJob = async (jobId) => postAllocationJobAction(jobId, 'cancel');
    const retryFailedAllocationItems = async (jobId) => postAllocationJobAction(jobId, 'retry');
    const retryAllocationSync = async (jobId) => postAllocationJobAction(jobId, 'retry-sync');
    const dismissAllocationFailures = async (jobId) => postAllocationJobAction(jobId, 'dismiss');

    /**
     * Get the impact of toggling an exclusion flag (how many items would be affected)
     * @param {number} userSetId - The user set ID
     * @param {string} flag - The exclusion flag name (e.g., 'exclude_minifigs')
     * @returns {Promise<Object>} - Object with count of affected items
     */
    const getExclusionImpact = async (userSetId, flag) => {
        try {
            const response = await apiRequest(`/userSets/${userSetId}/exclusion-impact?flag=${flag}`, 'GET');
            return response?.data || response;
        } catch (error) {
            console.error('Error getting exclusion impact:', error);
            throw handleError(error);
        }
    };

    /**
     * Update an exclusion flag on a user set (creates or removes set items accordingly)
     * @param {number} userSetId - The user set ID
     * @param {string} flag - The exclusion flag name (e.g., 'exclude_minifigs')
     * @param {boolean} value - The new value for the flag
     * @returns {Promise<Object>} - The update response
     */
    const updateExclusionFlag = async (userSetId, flag, value) => {
        try {
            const response = await apiRequest(`/userSets/${userSetId}/exclusion`, 'PUT', { flag, value });
            return response?.data || response;
        } catch (error) {
            console.error('Error updating exclusion flag:', error);
            throw handleError(error);
        }
    };

    /**
     * Add a "Listed On" marketplace indicator for a user set.
     * @param {number} userSetId
     * @param {string} marketplace - 'bricklink' or 'ebay'
     * @returns {Promise<Object>} The created listing row
     */
    const addListing = async (userSetId, marketplace) => {
        try {
            const response = await apiRequest(`/userSets/${userSetId}/listings`, 'POST', { marketplace });
            return response?.data || response;
        } catch (error) {
            console.error('Error adding listing:', error);
            throw handleError(error);
        }
    };

    /**
     * Remove a "Listed On" marketplace indicator for a user set.
     * @param {number} userSetId
     * @param {string} marketplace - 'bricklink' or 'ebay'
     * @returns {Promise<Object>} The removed listing row
     */
    const removeListing = async (userSetId, marketplace) => {
        try {
            const response = await apiRequest(`/userSets/${userSetId}/listings/${marketplace}`, 'DELETE');
            return response?.data || response;
        } catch (error) {
            console.error('Error removing listing:', error);
            throw handleError(error);
        }
    };

    /**
     * Get set matches for a list of lot IDs.
     * Returns a map of lotId -> array of matching user_set_items with set info.
     * @param {number[]} lotIds - Array of lot IDs
     * @returns {Promise<Object>} Map of lotId -> [{ user_set_id, user_set_item_id, identifier, name, number, qty_have, current_qty_needed, total_qty_needed }]
     */
    const getSetMatchesForLots = async (lotIds) => {
        try {
            const lotIdsParam = lotIds.join(',');
            const response = await apiRequest(
                `/userSets/set-matches?lot_ids=${encodeURIComponent(lotIdsParam)}`,
                'GET'
            );
            return response?.data || {};
        } catch (error) {
            logger.error('Error getting set matches for lots', 'getSetMatchesForLots', { error, lotIds });
            throw handleError(error);
        }
    };

    /**
     * Fetch the merged list of parts still needed across the given user sets.
     * The API groups by item type, item number and colour and sums the
     * quantities, so two sets that share a part return one row.
     *
     * @param {number[]} userSetIds
     * @param {Object} [options]
     * @param {boolean} [options.excludeOwned=false] - Exclude parts the user already owns in inventory
     * @returns {Promise<Array<Object>>}
     */
    const getWantedList = async (userSetIds, { excludeOwned = false } = {}) => {
        try {
            const idsParam = userSetIds.join(',');
            const excludeParam = excludeOwned ? '&exclude_owned=true' : '';
            const response = await apiRequest(
                `/userSets/wanted-list?user_set_ids=${encodeURIComponent(idsParam)}${excludeParam}`,
                'GET'
            );
            return response.data;
        } catch (error) {
            logger.error('Error getting wanted list', 'getWantedList', { error, userSetIds });
            throw handleError(error);
        }
    };

    return {
        getUserSets,
        getComponentTrackingPreview,
        trackSetItemComponents,
        addUserSet,
        updateUserSet,
        deleteUserSet,
        getUserSetItems,
        updateUserSetItem,
        updateVerification,
        deleteUserSetItems,
        allocateToSet,
        enqueueAllocationJob,
        getAllocationStatus,
        getAllocationJob,
        cancelAllocationJob,
        retryFailedAllocationItems,
        retryAllocationSync,
        dismissAllocationFailures,
        getExclusionImpact,
        updateExclusionFlag,
        addListing,
        removeListing,
        getSetMatchesForLots,
        getWantedList
    };
};

export default useUserSetsService; 
